// EarthTiles /v1 tile contract — CLIENT side, mirrored in the sim (TIL-1..3, E-38).
// PURE + isomorphic (ENG-1): schemas + decode + the ENU projection, no DOM/fetch. The
// game imports NO code from the EarthTiles project — it re-declares the contract here
// (ARCH §24 / ETD3) and validates every payload. A schema-invalid payload or a
// contract-major mismatch is a loud typed error, never a silent fallback (E-38).
//
// Tiles are intermediate geodata in raw WGS84 (lat, lon); the sim owns the ENU
// projection (TIL-2), matching `client/atlas/overpass.ts` `projectLatLon` exactly.
import { z } from 'zod';
import { TILE } from '../config';

/** Must match `client/atlas/overpass.ts` / `sim/atlas/elevation.ts` (the shared datum). */
const M_PER_DEG_LAT = 111_320;

export interface LatLon { lat: number; lon: number; }

// ── Contract schemas (mirror EarthTiles ARCH §2 SVC-2) ────────────────────────
const bboxSchema = z.tuple([z.number(), z.number(), z.number(), z.number()]); // [S,W,N,E]
const latLonPt = z.tuple([z.number(), z.number()]); // [lat, lon]

export const DEM_SOURCE_IDS = ['3dep-1m', 'srtm-30m'] as const;
export type DemSourceId = (typeof DEM_SOURCE_IDS)[number];
export const ROAD_CLASSES = ['motorway', 'primary', 'secondary', 'residential', 'footway'] as const;
export const AEROWAY_KINDS = ['runway', 'taxiway', 'apron', 'helipad'] as const;

export const metaSchema = z.strictObject({
  contract: z.strictObject({ major: z.number().int().positive(), minor: z.number().int().nonnegative() }),
  sources: z.record(z.string(), z.string()),
  attribution: z.array(z.string()),
  coverage: z.array(bboxSchema),
});
export type TileMeta = z.infer<typeof metaSchema>;

export const demTileSchema = z.strictObject({
  v: z.literal(1),
  kind: z.literal('dem'),
  source: z.enum(DEM_SOURCE_IDS),
  verticalDatum: z.enum(['navd88', 'egm96']),
  accuracyM: z.number().positive(),
  origin: z.strictObject({ lat: z.number(), lon: z.number() }),
  cellDeg: z.strictObject({ dLat: z.number().positive(), dLon: z.number().positive() }),
  w: z.number().int().positive(),
  h: z.number().int().positive(),
  data: z.string(),
  noData: z.number(),
});
export type DemTilePayload = z.infer<typeof demTileSchema>;

const roadSchema = z.strictObject({ points: z.array(latLonPt).min(2), class: z.enum(ROAD_CLASSES) });
const polygonSchema = z.strictObject({ points: z.array(latLonPt).min(3), holes: z.array(z.array(latLonPt).min(3)).optional() });
const buildingSchema = z.strictObject({ footprint: z.array(latLonPt).min(3), levels: z.number().int().min(1).max(64), holes: z.array(z.array(latLonPt).min(3)).optional() });
const aerowaySchema = z.strictObject({ kind: z.enum(AEROWAY_KINDS), points: z.array(latLonPt).min(2), widthM: z.number().positive().optional(), ref: z.string().optional() });

export const vectorTileSchema = z.strictObject({
  v: z.literal(1),
  kind: z.literal('vector'),
  roads: z.array(roadSchema),
  buildings: z.array(buildingSchema),
  water: z.array(polygonSchema),
  green: z.array(polygonSchema),
  aeroway: z.array(aerowaySchema),
  trees: z.array(latLonPt),
});
export type VectorTilePayload = z.infer<typeof vectorTileSchema>;

export const imagerySidecarSchema = z.strictObject({
  v: z.literal(1), kind: z.literal('imagery'), source: z.literal('naip'),
  capturedAt: z.string(), license: z.string(), sha256: z.string(),
});

// ── Typed failure (E-38): never a silent fallback ────────────────────────────
export type TileErrorKind = 'version' | 'schema' | 'unreachable';
export class TileContractError extends Error {
  readonly kind: TileErrorKind;
  constructor(kind: TileErrorKind, message: string) {
    super(message);
    this.name = 'TileContractError';
    this.kind = kind;
  }
}

/** Validate `/v1/meta` and check the contract major against what we speak (TIL-3).
 *  A major mismatch or a schema-invalid body → a loud typed error (E-38). */
export function parseMeta(json: unknown): TileMeta {
  const meta = parse(metaSchema, json, 'meta');
  if (meta.contract.major !== TILE.CONTRACT_MAJOR) {
    throw new TileContractError('version', `tile contract major ${meta.contract.major} ≠ supported ${TILE.CONTRACT_MAJOR}`);
  }
  return meta;
}

export const parseDemTile = (json: unknown): DemTilePayload => parse(demTileSchema, json, 'dem tile');
export const parseVectorTile = (json: unknown): VectorTilePayload => parse(vectorTileSchema, json, 'vector tile');

function parse<T>(schema: z.ZodType<T>, json: unknown, what: string): T {
  const r = schema.safeParse(json);
  if (!r.success) throw new TileContractError('schema', `invalid ${what}: ${r.error.issues[0]?.message ?? 'schema error'}`);
  return r.data;
}

// ── DEM data codec (base64 little-endian Float32, matches EarthTiles) ──────────
/** Decode a base64 little-endian Float32 grid (isomorphic: browser + node worker). */
export function decodeDemData(b64: string): Float32Array {
  const bin = typeof atob === 'function' ? atob(b64) : Buffer.from(b64, 'base64').toString('binary');
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  const dv = new DataView(bytes.buffer);
  const out = new Float32Array(bytes.length >> 2);
  for (let i = 0; i < out.length; i++) out[i] = dv.getFloat32(i * 4, true);
  return out;
}

// ── ENU projection (TIL-2; the sim owns it — matches overpass.ts projectLatLon) ─
/** Project (lat,lon) → local block coords [x, z] around `center` at `scale` m/block. */
export function projectLatLon(lat: number, lon: number, center: LatLon, scale: number): [number, number] {
  const mPerDegLon = M_PER_DEG_LAT * Math.cos((center.lat * Math.PI) / 180);
  const east = (lon - center.lon) * mPerDegLon;
  const north = (lat - center.lat) * M_PER_DEG_LAT;
  return [Math.round(east / scale) + 0, Math.round(-north / scale) + 0]; // z grows south
}

/** True when a DEM source is fine enough to justify the world's ±1-block claim at
 *  `scaleM` metres/block (ERT-3). The block-error `accuracyM / scaleM` must round to
 *  ≤ 1 block: 3dep-1m at the 0.9144 m default rounds to 1 (claims ±1); srtm-30m rounds
 *  to ~18 (no claim). (The spec's literal `accuracy ≤ scale` would fail 3dep-1m at
 *  default since 1 m > 0.9144 m, contradicting its own stated acceptance — see
 *  decisions.md §M39.) */
export function claimsBlockAccuracy(source: DemSourceId, scaleM: number): boolean {
  const accuracyM = TILE.ACCURACY_CLAIM_M[source] ?? Infinity;
  return Math.round(accuracyM / scaleM) <= 1;
}

// ── Slippy XYZ addressing (EPSG:3857; mirrors EarthTiles tile/scheme.ts) — the
//    client uses this to enumerate which tiles cover a world's extent (ERT-6). ──
export interface TileXY { x: number; y: number; }
const MERCATOR_LAT_LIMIT = 85.05112877980659;
const clampLat = (lat: number): number => (lat < -MERCATOR_LAT_LIMIT ? -MERCATOR_LAT_LIMIT : lat > MERCATOR_LAT_LIMIT ? MERCATOR_LAT_LIMIT : lat);

/** (lon,lat) → the XYZ tile containing it at zoom z. */
export function lonLatToTile(lon: number, lat: number, z: number): TileXY {
  const n = 2 ** z;
  const latRad = (clampLat(lat) * Math.PI) / 180;
  const clamp = (v: number): number => (v < 0 ? 0 : v >= n ? n - 1 : v);
  return {
    x: clamp(Math.floor(((lon + 180) / 360) * n)),
    y: clamp(Math.floor(((1 - Math.asinh(Math.tan(latRad)) / Math.PI) / 2) * n)),
  };
}

/** WGS84 bounds [S,W,N,E] of slippy tile (x,y) at zoom z (inverse of `lonLatToTile`;
 *  mirrors EarthTiles tile/scheme.ts). The imagery bake (OVL-4) uses it to geo-place a
 *  decoded NAIP tile. */
export function tileBoundsWgs84(x: number, y: number, z: number): [number, number, number, number] {
  const n = 2 ** z;
  const lon = (xt: number): number => (xt / n) * 360 - 180;
  const lat = (yt: number): number => (Math.atan(Math.sinh(Math.PI * (1 - (2 * yt) / n))) * 180) / Math.PI;
  const west = lon(x), east = lon(x + 1);
  const north = lat(y), south = lat(y + 1); // y grows south
  return [south, west, north, east];
}

/** Every tile (x,y) at zoom z intersecting bbox [S,W,N,E], row-major. */
export function tilesForBbox(bbox: readonly [number, number, number, number], z: number): TileXY[] {
  const [south, west, north, east] = bbox;
  const nw = lonLatToTile(west, north, z), se = lonLatToTile(east, south, z);
  const out: TileXY[] = [];
  for (let y = Math.min(nw.y, se.y); y <= Math.max(nw.y, se.y); y++) {
    for (let x = Math.min(nw.x, se.x); x <= Math.max(nw.x, se.x); x++) out.push({ x, y });
  }
  return out;
}
